/**
 * Copyright (c) 2019 - 2023, all rights reserved.
 * State Of New Jersey Department Of Human Services
 * Division Of Family Development
 *
 * File: "data-store.service.ts"
 * Project: "NJFLOURISH"
 * File Created: "date created"
 * Author: "PROTECH"
 * Description: "Used to Load required Data store required for UI"
 *
 * History
 * -------
 * Last Modified: "date last modified"
 * Modified By: "PROTECH"
 * Update Comments: "reason for modification"
 * -----
 */
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

import { OutData, OutRecordsListData, Status } from "@mynjhelps/common";
import { ApiService } from "@mynjhelps/http";
import { MessageService } from "@mynjhelps/message";
import { StorageService } from "@mynjhelps/storage";
import { UtilityService } from "@mynjhelps/utility";
import { HttpClient } from "@angular/common/http";
import {
  GetRefScreenLookupRecordData
} from "../model";
import { ScreenRecordData } from "@shared/model/screen/screen-record-data.model";
import { ScreenFunctionRecordData } from "@shared/model/screen/screen-function-record-data.model";
import { SiteUpTimeData } from "@shared/model/logon/siteup-time-data.model";
import { EncryptionService } from "@mynjhelps/authentication";
import { appConfig } from "src/config";

@Injectable()
export class DataStoreService {
  constructor(
    private apiService: ApiService,
    private messageService: MessageService,
    private storageService: StorageService,
    private encryptionService: EncryptionService,

    private httpClient: HttpClient
  ) {}

  /**
   * Load REFM Store and return Promise
   * @returns Promise
   */

  // loadStore(isReload?: boolean): Promise<void> {
  //   return new Promise((resolve, reject) => {
  //     if (!isReload && this.storageService.getItem("STORE")) {
  //       return resolve();
  //     }
  //     this.getStore().subscribe((result: any) => {
  //       this.storageService.setItem("STORE", JSON.stringify(result.STORE));
  //       resolve();
  //     });
  //   });
  // }

  loadCCAPInfo(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getCCAPInfo().subscribe((result: any) => {
        this.storageService.setItem("CCAP", result.ReturnValue);
        resolve();
      });
    });
  }

  /**
   * Load Error Message Store to Show in the UI
   * @returns Promise
   */
  loadEmsg(): Promise<void> {
    return new Promise((resolve, reject) => {
      const emgStore = this.storageService.getItem("EMSG");
      if (emgStore !== "{}") {
        return resolve();
      }

      this.getEmsg().subscribe((result: any) => {
      
        result.validateResponse(
          () => {
            let emsg = {
              EN: {},
              ES: {}
            };
            let emsgRecord = result.RecordsList;
            for (
              let index = 0, emsgLength = emsgRecord.length;
              index < emsgLength;
              index++
            ) {
              if (emsgRecord[index].LanguageCode == "EN") {
                emsg[emsgRecord[index].ErrorCode] =
                  emsgRecord[index].DescriptionErrorText;
              }            
            }

            this.storageService.setItem("EMSG", JSON.stringify(emsg));

            resolve();
          },
          () => {
            resolve();
          }
        );
      });
    });
  }

  /**
   * Load Current System Date
   * @returns Promise
   */
  loadSystemDate(): Promise<void> {
    return new Promise((resolve, reject) => {
      resolve();
    });
  }

  loadUserInfo(): Promise<void> {
    return new Promise((resolve, reject) => {
      resolve();
    });
  }

  loadEnJson(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.httpClient.get("assets/i18n/en.json").subscribe((data) => {
        // console.log("en json loaded ... *****************************");
        resolve();
      });
    });
  }
  
  loadEsJson(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.httpClient.get("assets/i18n/es.json").subscribe((data) => {
        // console.log("es json loaded ... *****************************");
        resolve();
      });
    });
  }

  loadRscl(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.storageService.getItem("RSCL")) {
        return resolve();
      }
      this.getRsclData().subscribe((result: any) => {
        result.validateResponse(
          () => {
            let groupedScreenData = [];
            if (result.RecordsList) {
              groupedScreenData = UtilityService.convertGrouping(
                result.RecordsList,
                "ScreenID"
              );
            }

            let rsclData = {};
            for (
              let scrnIndex = 0, screenDataLength = groupedScreenData.length;
              scrnIndex < screenDataLength;
              scrnIndex++
            ) {
              const screenData = groupedScreenData[scrnIndex].children;
              for (
                let fieldIndex = 0, fieldLength = screenData.length;
                fieldIndex < fieldLength;
                fieldIndex++
              ) {
                const screenFieldData = screenData[
                  fieldIndex
                ] as GetRefScreenLookupRecordData;
                if (screenFieldData.DependentValueText.isEmpty()) {
                  rsclData[
                    screenFieldData.ScreenID + "_" + screenFieldData.FieldName
                  ] = screenFieldData;
                } else {
                  rsclData[
                    screenFieldData.ScreenID +
                      "_" +
                      screenFieldData.FieldName +
                      "_" +
                      screenFieldData.DependentValueText
                  ] = screenFieldData;
                }
              }
            }
            this.storageService.setItem("RSCL", JSON.stringify(rsclData));
            resolve();
          },
          () => {
            reject();
          }
        );
      });
    });
  }

  loadTextTransform(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.storageService.getItem("TRANSFORM")) {
        return resolve();
      }
      this.getTextTransformData().subscribe((result: any) => {
        result.validateResponse(
          () => {
            let store = {};
            if (result && result.RecordsList) {
              for (
                let index = 0, dataLength = result.RecordsList.length;
                index < dataLength;
                index++
              ) {
                const data = result.RecordsList[index];

                if (store[data.DataFieldText] == undefined) {
                  store[data.DataFieldText] = {};
                }
                store[data.DataFieldText] = data;
                store[data.DataFieldText]["index"] = index;
              }
            }

            this.storageService.setItem("TRANSFORM", JSON.stringify(store));
            resolve();
          },
          () => {
            reject();
          }
        );
      });
    });
  }

  loadRefm(isReload?: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.storageService.getItem("STORE")) {
        return resolve();
      }

      this.getRefmData().subscribe((result: any) => {
        result.validateResponse(
          () => {
            let store = {
              REFM: {
                EN: {},
                ES: {}
              }
            };
            if (result && result.RecordsList) {
              for (
                let index = 0, dataLength = result.RecordsList.length;
                index < dataLength;
                index++
              ) {
                const data = result.RecordsList[index];

                if (data.LanguageCode == "EN") {
                  if (store.REFM[data.TableID + data.TableSubID] == undefined) {
                    store.REFM[data.TableID + data.TableSubID] = [];
                  }

                  store.REFM[data.TableID + data.TableSubID].push({
                    label: data.DescriptionValueText,
                    value: data.ValueCode
                  });
                }

                if (
                  store.REFM[data.LanguageCode][
                    data.TableID + data.TableSubID
                  ] == undefined
                ) {
                  store.REFM[data.LanguageCode][
                    data.TableID + data.TableSubID
                  ] = [];
                }

                store.REFM[data.LanguageCode][
                  data.TableID + data.TableSubID
                ].push({
                  label: data.DescriptionValueText,
                  value: data.ValueCode
                });
              }
            }
            this.storageService.setItem("STORE", JSON.stringify(store));
            resolve();
          },
          () => {
            reject();
          }
        );
      });
    });
  }

  /**
   * Load All store and Bootstrap Application thru
   * @returns Promise
   */ //this.loadPublicKey(),
  loadDataStore(): Promise<void[]> {
    return Promise.all([
      this.loadRefm(),
      this.loadTextTransform(),
      this.loadEmsg(),
      this.loadRscl(),
      this.loadCCAPInfo()
    ]);
  }
  /**
   * Get REFM Data
   * @returns Observable
   */

  getPublicKey() {
    return this.apiService.Post({}, "Utility/GetPublicKey");
  }


  getRefmData() {
    return this.apiService.Post({}, "DataStore/GetRefmLookup");
  }

  getTextTransformData() {
    return this.apiService.Post({}, "DataStore/GetTextTransformType");
  }

  getRsclData() {
    return this.apiService.Post({}, "DataStore/GetRefmScreenLookup");
  }

  getCCAPInfo() {
    return this.apiService.Post(
      {
        TableID: "PRGM",
        TableSubID: "CHLD"
      },
      "Utility/GetReferenceValue"
    );
  }

  /**
   * Get Error Message Data
   * @returns Observable
   */
  getEmsg() {
    return this.apiService.Post({}, "DataStore/GetMessages");
  }

  /**
   * Get Current System Date from App Server
   * @returns Observable
   */
  getCurrentDateTime() {
    return this.apiService.Post({}, "Anonymous/GetSystemDate", "", {}, true);
  }

  errorLog(input) {
    return this.apiService.Post(input, "Anonymous/ExternalLog1", "", {}, true);
  }

  getUserInfo() {}

  getScreenList() {
    return this.apiService.Get("childsupport-api/api/screen/screens");
  }

  getScreenFunctionList(
    screenName: string
  ): Observable<OutData<ScreenFunctionRecordData[]>> {
    return this.apiService.Get(
      "childsupport-api/api/screen/{0}/screenfunction".format(screenName)
    );
  }

  getOperationHour(): Observable<OutData<SiteUpTimeData>> {
    return this.apiService.Post({}, "Anonymous/RetreiveOperationalHours");
  }
}
